import Layout from '@/component/Layout/Layout'
import LottoBannerCard from '@/ui-components/components/BetLotto/LottoBannerCard'
import { getLottoNameAndCountryFromLottoGame } from '@/utils/lotto-type-v2'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useGameData } from './hooks/useGameData'
import { TabType } from '@/ui-components/components/BetLotto/BetLotto.types'
import { MainTabs } from '@/ui-components/components/BetLotto/MainTabs/MainTabs'
import {
  getRuleData,
  isEncryptLotto,
  isYeekeeType,
} from '@/utils/lotto-type-v2'
import { BetPanel } from '@/ui-components/components/BetLotto/BetPanel/BetPanel'
import { PanelTabs } from '@/ui-components/components/BetLotto/PanelTabs/PanelTabs'
import { PanelType } from '@/ui-components/components/BetLotto/BetLotto.types'
import useBetControl from './hooks/useBetControl'
import { BetControls } from '@/ui-components/components/BetLotto/BetControls/BetControls'
import Rules from '@/ui-components/components/BetLotto/Rules'
import { useTranslation } from 'react-i18next'
import { formatPrice } from '@/utils/number-format'
import PanelSelection from '@/ui-components/components/BetLotto/PanelSelection/PanelSelection'
import useSelectPanel from './hooks/useSelectPanel'
import ManualEntry from '@/ui-components/components/BetLotto/ManualEntry'
import useManualBet from './hooks/useManualBet'
import Signature from '@/ui-components/components/BetLotto/Signature/Signature'
import { RENDER_RESULT_MAP } from '@/page/result/hooks/useResultPage'
import {
  renderThaiGovernmentComponent,
  renderStockComponent,
  renderLottoGroupComponent,
} from '@/page/result/hooks/useResultPage'
import { Game } from '@/redux/reducers/gameReducer'
import ShootTab from './tabComponents/ShootTab'
import YeekeeMaltaResult from '@/ui-components/components/BetLotto/YeekeeMaltaResult/YeekeeMaltaResult'
import { LAYOUT_CLASS } from '@/ui-components/components/BetLotto/BetLotto.constants'
import { trans } from '@/i18n/utils'
import { safeSetTimeout } from '@/utils/js-core-utils'
import { getBackgroundFlagGroup } from '@/utils/flag-utils'
import gamePageStyles from '@/ui-components/pages/GamePage.module.css'

// Selecting game data
const LottoPage: React.FC = () => {
  const { gameId, roundId } = useParams()

  const {
    game,
    round,
    loading,
    roundGameTitle: title,
  } = useGameData({ gameId, roundId })
  const isResulted = round?.is_result_confirmed || false
  const {
    lottoBetList,
    onChangeSingleBetPrice,
    onDeleteItem,
    onClearAll,
    totalBetAmount,
    creditLeft,
    onChangeBetPrice,
    addLottoListAndHandleResponse,
    onDeleteByNumberAndType,
    dialogComponent,
    limitDialogComponent,
    handleBetSubmit,
    currencyObject,
    betSetNumber,
  } = useBetControl()

  const {
    DIGIT_MODES,
    handleSelectDigitMode,
    digitMode,
    betTypeOptions,
    activeBetType,
    handleSelectBetType,
    getLimitTypeUI,
  } = useSelectPanel()
  const [mainTab, setMainTab] = useState<TabType>('bet')
  const [activeTab, setActiveTab] = useState<PanelType>('panel')
  const [isGameActive, setIsGameActive] = useState<boolean>(true)
  const { i18n } = useTranslation()

  const {
    betTypes,
    selectedBetTypes,
    handleSelectBetType: handleSelectBetTypeOnManualEntry,
    inputDigit,
    handleSelectBetNumber,
  } = useManualBet(addLottoListAndHandleResponse)

  const isLoading = loading || !game || !round || !gameId || !roundId
  const closeAt = round?.close_at

  // Check if game is still active based on close time
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null

    if (round?.close_at) {
      const now = new Date()
      const closeTime = new Date(round.close_at)

      // Check if already closed
      if (now > closeTime) {
        setIsGameActive(false)
      } else {
        // It's possible that round data is from previous page
        // so set it as true if round is not closed
        setIsGameActive(true)

        // Calculate time until close
        const timeUntilClose = closeTime.getTime() - now.getTime()
        // Set timeout to update state when game closes
        timeoutId =
          safeSetTimeout(() => {
            setIsGameActive(false)
          }, timeUntilClose) || null
      }
    }

    // Clean up timeout on unmount or when closeAt changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [round])

  const { country, name: flagBannerName } = getLottoNameAndCountryFromLottoGame(
    game?.type || '',
    game?.flag || '',
  )

  const isYeekee = isYeekeeType(game?.type || '')

  const isEncrypt = isEncryptLotto(game?.type || '')
  const now = new Date()
  const showSignature =
    isEncrypt &&
    round?.signature &&
    new Date(round.close_at) > now &&
    round.is_result_confirmed === false

  const renderTermsSections = () => {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: trans(i18n, game?.translations || [], 'description'),
        }}
      />
    )
  }

  const payoutRates: Parameters<typeof Rules>[0]['payoutRates'] = getRuleData(
    round || undefined,
  ).map((item) => ({
    type: item.bet_type,
    price: formatPrice(item.bet_price, i18n),
  }))
  const renderResultCard = () => {
    // Check if game type matches with RENDER_RESULT_MAP
    const matchedKey = (
      Object.entries(RENDER_RESULT_MAP) as [
        string,
        { type: readonly string[] },
      ][]
    ).find(([, config]) => config.type.includes(game?.type || ''))?.[0]

    if (isResulted && matchedKey) {
      let component: React.ReactNode
      const gameInjectedResult = { ...game, rounds: [round] } as unknown as Game
      switch (matchedKey) {
        case 'yeekee':
        case 'stock':
          component = renderStockComponent(gameInjectedResult)
          return (
            <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
              {component}
            </div>
          )
        case 'government':
          component = renderThaiGovernmentComponent(gameInjectedResult)
          return (
            <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
              {component}
            </div>
          )
        case 'group':
          component = renderLottoGroupComponent(gameInjectedResult)
          return (
            <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
              {component}
            </div>
          )
      }
    }
    return (
      <div className={`w-full ${gamePageStyles.bannerWrapper}`}>
        <LottoBannerCard
          country={country}
          flagBackgroundName={getBackgroundFlagGroup(flagBannerName)}
          title={title}
          timeRemaining={closeAt || ''}
          isCancelled={round?.status === 'cancelled'}
        />
      </div>
    )
  }

  return (
    <Layout loading={isLoading}>
      <div className={`${LAYOUT_CLASS.CONTAINER} ${gamePageStyles.container}`}>
        {renderResultCard()}

        <div className={LAYOUT_CLASS.ROW}>
          <MainTabs
            onTabChange={setMainTab}
            isYeekee={isYeekee}
            isResulted={isResulted}
            isEncrypt={isEncrypt}
            isGameActive={isGameActive}
          />
        </div>

        <div className={LAYOUT_CLASS.ROW}>
          {mainTab === 'bet' && !isResulted ? (
            <>
              <div className={LAYOUT_CLASS.GRID_TWO_COLUMNS}>
                <BetPanel>
                  <PanelTabs activeTab={activeTab} onTabChange={setActiveTab} />
                  {showSignature && (
                    <Signature signature={round?.signature ?? ''} />
                  )}
                  {activeTab === 'panel' ? (
                    <PanelSelection
                      DIGIT_MODES={DIGIT_MODES}
                      handleSelectDigitMode={handleSelectDigitMode}
                      digitMode={digitMode}
                      betTypeOptions={betTypeOptions}
                      activeBetType={activeBetType}
                      handleSelectBetType={handleSelectBetType}
                      betList={lottoBetList}
                      addLottoListAndHandleResponse={
                        addLottoListAndHandleResponse
                      }
                      onDeleteByNumberAndType={onDeleteByNumberAndType}
                      getLimitTypeUI={getLimitTypeUI}
                    />
                  ) : (
                    <ManualEntry
                      inputDigit={inputDigit}
                      betTypes={betTypes}
                      selectedBetTypes={selectedBetTypes}
                      onBetTypeSelect={handleSelectBetTypeOnManualEntry}
                      onBetNumberSelect={handleSelectBetNumber}
                    />
                  )}
                </BetPanel>

                <BetControls
                  onChangeBetPrice={onChangeBetPrice}
                  betData={lottoBetList}
                  onChangeSingleBetPrice={onChangeSingleBetPrice}
                  onDeleteItem={onDeleteItem}
                  onClearAll={onClearAll}
                  totalBetAmount={Number(totalBetAmount)}
                  creditLeft={creditLeft}
                  onBetSubmit={handleBetSubmit}
                  pollListUrl={`/lotto/poll`}
                  betSetNumber={betSetNumber}
                  currencyObject={currencyObject}
                />
              </div>
            </>
          ) : mainTab === 'shoot' ? (
            <ShootTab round={round || undefined} isResulted={isResulted} />
          ) : mainTab === 'result' ? (
            <YeekeeMaltaResult
              signature={round?.signature ?? ''}
              encryptRawResult={round?.encrypt_raw_result ?? ''}
              rawResult={round?.six_result ?? ''}
              encryptResult={round?.encrypt_result ?? ''}
            />
          ) : (
            <Rules
              payoutRates={payoutRates}
              termsContent={renderTermsSections()}
            />
          )}
        </div>
        {dialogComponent}
        {limitDialogComponent}
      </div>
    </Layout>
  )
}

export default LottoPage
